import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, Injector, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxAmountForm, OnyxOptional } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxInputLabelComponent,
  OnyxModalComponent,
  OnyxTextFieldComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { chain } from 'lodash';
import type { Observable } from 'rxjs';
import {
  ReplaySubject,
  Subject,
  catchError,
  combineLatest,
  filter,
  forkJoin,
  map,
  of,
  startWith,
} from 'rxjs';
import { RoutePreferencesFormComponent } from '../../../../../common/components/forms/route-preferences-form/route-preferences-form.component';
import { CalculateRouteFeature } from '../../../../../common/enums/calculate-route-feature';
import type { DictionaryType } from '../../../../../common/enums/dictionary-code';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { FuelType } from '../../../../../common/enums/fuel-type';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import type { RouteConfig } from '../../../../../common/interfaces/address/calculate-route-request';
import { DataProvidersService } from '../../../../../common/services/data-providers.service';
import { DictionariesService } from '../../../../../common/services/dictionaries.service';
import { PreferencesService } from '../../../../../common/services/preferences.service';
import { FleetCategory } from '../../../../fleet/common/enums/fleet-category';

export interface OrderFormRouteConfigModalData {
  routeConfig?: OnyxOptional<RouteConfig>;
  disableVehicleCategory?: boolean;
}

export type OrderFormRouteConfigModalResult = RouteConfig;

export type OrderFormRouteConfigForm = ReturnType<
  ReturnType<typeof OrderFormRouteConfigModalComponent.buildForm>['getRawValue']
>;

@Component({
  selector: 'app-order-form-route-config-modal',
  standalone: true,
  imports: [
    OnyxModalComponent,
    ReactiveFormsModule,
    TranslatePipe,
    OnyxButtonComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxInputLabelComponent,
    OnyxToggleComponent,
    OnyxFormGroupComponent,
    OnyxTextFieldComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './order-form-route-config-modal.component.html',
  styleUrl: './order-form-route-config-modal.component.scss',
})
export class OrderFormRouteConfigModalComponent {
  protected readonly data = inject<OrderFormRouteConfigModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly translateService = inject(TranslateService);
  protected readonly preferencesService = inject(PreferencesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  protected readonly FEATURES = chain(CalculateRouteFeature)
    .orderBy((feature) =>
      this.translateService.instant(
        `${DictionaryCode.CALCULATE_ROUTE_FEATURE}.${feature}`,
      ),
    )
    .value();

  protected readonly DictionaryCode = DictionaryCode;

  protected readonly vehicleCategories$ =
    this.dictionariesService.getDictionary(DictionaryCode.VEHICLE_CATEGORY);
  protected readonly fuelTypes$ = this.dictionariesService
    .getDictionary(DictionaryCode.FUEL_TYPE)
    .pipe(
      map((options) =>
        chain(options)
          .groupBy((option) => option.averageSemiTruckFuelConsumption !== 0)
          .thru((groups) => [
            { id: '0.default', options: groups['true'] ?? [] },
            {
              id: '1.other',
              options: (groups['false'] ?? []).map((option) => ({
                ...option,
                tooltip: 'labels.unsupportedFuelType',
                disabled: true,
              })),
            },
          ])
          .value(),
      ),
    );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );
  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  protected form = OrderFormRouteConfigModalComponent.buildForm(
    this.fb,
    this.preferencesService,
  );
  protected loading = signal(false);

  protected vehicleCategory$ = new ReplaySubject<
    DictionaryType<DictionaryCode.VEHICLE_CATEGORY>[number]['value'] | null
  >(1);
  protected fuelType$ = new ReplaySubject<
    DictionaryType<DictionaryCode.FUEL_TYPE>[number] | null
  >(1);
  protected close$ = new Subject<OrderFormRouteConfigModalResult | void>();

  protected fuelAverageConsumptionUnit$ = this.fuelType$.pipe(
    startWith(null),
    map((fuelType) => `${fuelType?.units[0] ?? ''}/100 km`),
  );
  protected fuelPriceLabel$ = this.fuelType$.pipe(
    startWith(null),
    map((fuelType) => {
      if (!fuelType) return 'labels.fuelPrice';
      return `${this.translateService.instant('labels.fuelPrice')} (${fuelType.units[0]})`;
    }),
  );

  constructor() {
    this.reset(this.data.routeConfig);

    ValidationHelper.toggleControls(this.form.controls.vehicleCategory, {
      enabled: !this.data.disableVehicleCategory,
      reset: false,
    });

    let first = true;
    combineLatest([this.fuelType$, this.vehicleCategory$])
      .pipe(
        filter((data) => data.every((value) => value != null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([fuelType, vehicleCategory]) => {
        const averageFuelConsumption =
          OrderFormRouteConfigModalComponent.getAverageFuelConsumption(
            fuelType!,
            vehicleCategory!,
          );

        const controls = this.form.controls;
        const shouldSetValue =
          !first || !this.data?.routeConfig?.fuelAverageConsumption;
        first = false;

        if (!shouldSetValue) return;

        controls.fuelAverageConsumption.setValue(averageFuelConsumption);

        ValidationHelper.toggleControls(
          [controls.fuelAverageConsumption, controls.fuelPrice],
          { enabled: averageFuelConsumption !== 0, reset: true },
        );
      });
  }

  protected restoreDefaults(): void {
    if (this.loading()) return;

    this.loading.set(true);
    OrderFormRouteConfigModalComponent.getDefaultConfig(this.injector, {
      vehicleCategory: this.data?.routeConfig?.vehicleCategory,
      vehicleTypes: [],
      resultCurrency: this.data?.routeConfig?.resultCurrency,
    })
      .subscribe((config) => this.reset(config))
      .add(() => this.loading.set(false));
  }

  protected async save(): Promise<void> {
    if (this.loading()) return;
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const value = this.form.getRawValue();
    const resultCurrency =
      value?.fuelPrice?.currency ?? this.preferencesService.defaultCurrency();

    this.close$.next(
      OrderFormRouteConfigModalComponent.toDto(
        value,
        this.preferencesService,
        resultCurrency,
      ),
    );
  }

  private reset(config: OnyxOptional<RouteConfig>): void {
    FormHelper.reset(this.form);
    this.form.setValue(
      OrderFormRouteConfigModalComponent.fromDto(
        config,
        this.preferencesService,
      ),
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      ...RoutePreferencesFormComponent.buildForm(fb, null).controls,
      fuelType: fb.control(preferencesService.defaultFuelType(), [
        Validators.required,
      ]),
      fuelAverageConsumption: fb.control<number | null>(null, [
        Validators.required,
      ]),
      fuelPrice: FormHelper.buildAmountForm(fb, preferencesService, {
        optional: true,
      }),
    });
  }

  public static toDto(
    value: OrderFormRouteConfigForm,
    preferencesService: PreferencesService,
    resultCurrency?: string | null,
  ): RouteConfig {
    return {
      ...RoutePreferencesFormComponent.toDto(value),
      fuelType: value.fuelType!,
      fuelAverageConsumption: value.fuelAverageConsumption!,
      fuelPrice: value.fuelPrice.value
        ? (value.fuelPrice as OnyxAmountForm)
        : undefined,
      resultCurrency: resultCurrency ?? preferencesService.defaultCurrency(),
    };
  }

  public static fromDto(
    config: OnyxOptional<RouteConfig>,
    preferencesService: PreferencesService,
  ) {
    return {
      ...RoutePreferencesFormComponent.fromDto(config ?? {}),
      fuelType: config?.fuelType ?? preferencesService.defaultFuelType(),
      fuelAverageConsumption: config?.fuelAverageConsumption ?? null,
      fuelPrice: {
        date: config?.fuelPrice?.date ?? null,
        value: config?.fuelPrice?.value ?? null,
        currency:
          config?.fuelPrice?.currency ??
          config?.resultCurrency ??
          preferencesService.defaultCurrency(),
      },
    };
  }

  public static getDefaultConfig(
    injector: Injector,
    options: {
      vehicleCategory?:
        DictionaryType<DictionaryCode.VEHICLE_CATEGORY>[number]['value'] | null;
      vehicleTypes: string[];
      resultCurrency?: string | null;
    },
  ): Observable<RouteConfig> {
    const dictionariesService = injector.get(DictionariesService);
    const preferencesService = injector.get(PreferencesService);
    const dataProvidersService = injector.get(DataProvidersService);

    const [countryCode, currency, fuelType] = [
      preferencesService.defaultCountry(),
      preferencesService.defaultCurrency(),
      preferencesService.defaultFuelType(),
    ];
    const vehicleCategory =
      options.vehicleCategory ??
      (options.vehicleTypes.every((type) =>
        type.startsWith(`${FleetCategory.VAN}-`),
      )
        ? FleetCategory.VAN
        : FleetCategory.SEMI_TRUCK);

    const fuelAverageConsumption$ = dictionariesService
      .getDictionary(DictionaryCode.FUEL_TYPE)
      .pipe(
        map((fuelTypes) => fuelTypes.find(({ value }) => value === fuelType)!),
        map((fuelTypeData) =>
          OrderFormRouteConfigModalComponent.getAverageFuelConsumption(
            fuelTypeData,
            vehicleCategory,
          ),
        ),
      );
    const fuelPrice$ = dataProvidersService.getFuelPrices().pipe(
      map(({ date, prices }): OnyxAmountForm | undefined => {
        const data = prices[countryCode]?.[currency];
        if (!data) return undefined;

        const value = (() => {
          switch (fuelType) {
            case FuelType.DIESEL:
            case FuelType.DIESEL_LNG:
              return data.diesel.price;

            case FuelType.LNG:
            case FuelType.LNG_DIESEL:
            case FuelType.PHEV:
            case FuelType.EV:
              return undefined;
          }
        })();
        if (!value) return undefined;

        return {
          date,
          value: Math.round(value * 100),
          currency,
        };
      }),
      catchError(() => of(undefined)),
    );

    return forkJoin([fuelAverageConsumption$, fuelPrice$]).pipe(
      map(([fuelAverageConsumption, fuelPrice]) => ({
        vehicleCategory,
        fuelType,
        fuelAverageConsumption,
        fuelPrice,
        resultCurrency: options?.resultCurrency ?? currency,
      })),
    );
  }

  private static getAverageFuelConsumption(
    fuelTypeData: DictionaryType<DictionaryCode.FUEL_TYPE>[number],
    vehicleCategory: DictionaryType<DictionaryCode.VEHICLE_CATEGORY>[number]['value'],
  ): number {
    switch (vehicleCategory) {
      case FleetCategory.SEMI_TRUCK:
        return fuelTypeData.averageSemiTruckFuelConsumption;
      case FleetCategory.STRAIGHT_TRUCK:
        return fuelTypeData.averageStraightTruckFuelConsumption;
      case FleetCategory.VAN:
        return fuelTypeData.averageVanFuelConsumption;
    }
  }
}
